import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { usePaginatedtrips } from "../hooks";
import { api } from "../api";
import { fmtDate, SC, I } from "../utils";
import {
  useToast,
  useConfirm,
  Btn,
  Card,
  Icon,
  Spinner,
  ApiError,
  Empty,
  Modal,
  Badge,
  Pages,
  Inp,
  Sel,
  Txa,
} from "../ui";
// ⚠️ Make sure ClientsTab.jsx ends with: export { ClientDetailModal };
import { ClientDetailModal } from "../clients/ClientsTab";
// Client edit + task modals, so the buttons inside ClientDetailModal actually work here.
import ClientFormModal from "../clients/ClientFormModal";
import QuickTaskModal from "../clients/QuickTaskModal";
import {
  TRIP_STATUS_MAP,
  tripTemplateToTripData,
  normaliseDestinations,
  findTripIndexForPackage,
} from "../clients/constants";
import { tripLabelLong } from "../ClientTripSelect";

// ─── STATUS MAPS ──────────────────────────────────────────────────────────────
const SBGR = {
  planning: "from-sky-400 to-blue-500",
  confirmed: "from-emerald-400 to-teal-500",
  ongoing: "from-violet-400 to-purple-500",
  completed: "from-slate-400 to-slate-500",
  cancelled: "from-rose-400 to-red-500",
};
const HERO = {
  planning: "from-sky-500 to-blue-600",
  confirmed: "from-emerald-500 to-teal-600",
  ongoing: "from-violet-500 to-purple-600",
  completed: "from-slate-500 to-slate-600",
  cancelled: "from-rose-500 to-red-600",
};
const STATUS_PILL = {
  planning: "bg-sky-100 text-sky-700",
  confirmed: "bg-emerald-100 text-emerald-700",
  ongoing: "bg-violet-100 text-violet-700",
  completed: "bg-slate-100 text-slate-600",
  cancelled: "bg-rose-100 text-rose-600",
};
// ─── TRIP TYPE MAPS ───────────────────────────────────────────────────────────
const TRIP_TYPES = [
  { value: "all", label: "All Types", emoji: "✈️" },
  { value: "private", label: "Private Trips", emoji: "🧳" },
  { value: "group", label: "Group Trips", emoji: "👥" },
];
const TRIP_TYPE_OPTIONS = TRIP_TYPES.filter((t) => t.value !== "all").map(
  (t) => ({
    value: t.value,
    label: `${t.emoji} ${t.label}`,
  }),
);
const TRIP_TYPE_PILL = {
  family: "bg-pink-100 text-pink-700",
  honeymoon: "bg-rose-100 text-rose-700",
  corporate: "bg-blue-100 text-blue-700",
  solo: "bg-slate-100 text-slate-700",
  group: "bg-violet-100 text-violet-700",
  adventure: "bg-orange-100 text-orange-700",
  pilgrimage: "bg-emerald-100 text-emerald-700",
  cruise: "bg-sky-100 text-sky-700",
  other: "bg-slate-100 text-slate-500",
};
const getTripTypeInfo = (value) =>
  TRIP_TYPES.find((t) => t.value === value) || { label: value, emoji: "✈️" };

// ─── TRIP FORM ────────────────────────────────────────────────────────────────
function TripForm({ initial = {}, onSubmit, onClose, saving }) {
  const [f, sf] = useState({
    tripName: initial.tripName || "",
    destinations: initial.destinations?.join(", ") || "",
    startDate: initial.startDate ? initial.startDate.split("T")[0] : "",
    endDate: initial.endDate ? initial.endDate.split("T")[0] : "",
    status: initial.status || "planning",
    tripType: initial.tripType || "other",
    hotel: initial.hotel?.join(", ") || "",
    toursBooked: initial.toursBooked?.join(", ") || "",
    notes: initial.notes || "",
  });
  const set = (k) => (e) => sf((p) => ({ ...p, [k]: e.target.value }));
  const sp = (v) =>
    v
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          ...f,
          destinations: sp(f.destinations),
          hotel: sp(f.hotel),
          toursBooked: sp(f.toursBooked),
        });
      }}
      className="space-y-4"
    >
      <Inp
        label="Trip Name *"
        placeholder="Dubai Family Escape 2025"
        value={f.tripName}
        onChange={set("tripName")}
        required
      />
      <Inp
        label="Destinations (comma separated)"
        placeholder="Dubai, Abu Dhabi"
        value={f.destinations}
        onChange={set("destinations")}
      />
      <Inp
        label="Start Date"
        type="date"
        value={f.startDate}
        onChange={set("startDate")}
      />
      <Inp
        label="End Date"
        type="date"
        value={f.endDate}
        onChange={set("endDate")}
        min={f.startDate || undefined}
      />
      <Sel
        label="Status"
        value={f.status}
        onChange={set("status")}
        options={[
          { value: "planning", label: "Planning" },
          { value: "confirmed", label: "Confirmed" },
          { value: "ongoing", label: "Ongoing" },
          { value: "completed", label: "Completed" },
          { value: "cancelled", label: "Cancelled" },
        ]}
      />
      <Sel
        label="Trip Type"
        value={f.tripType}
        onChange={set("tripType")}
        options={TRIP_TYPE_OPTIONS}
      />
      <Inp
        label="Hotels (comma separated)"
        placeholder="Hilton Dubai, Rotana"
        value={f.hotel}
        onChange={set("hotel")}
      />
      <Inp
        label="Tours Booked (comma separated)"
        placeholder="Desert Safari, City Tour"
        value={f.toursBooked}
        onChange={set("toursBooked")}
      />
      <Txa
        label="Notes"
        placeholder="Special requirements…"
        value={f.notes}
        onChange={set("notes")}
      />
      <div className="flex gap-2">
        <Btn
          type="submit"
          variant="primary"
          className="flex-1"
          loading={saving}
        >
          Save Trip
        </Btn>
        <Btn type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Btn>
      </div>
    </form>
  );
}

// ─── TRIP DETAIL MODAL ────────────────────────────────────────────────────────
function TripDetailModal({
  trip: init,
  onClose,
  onEdit,
  onDeleted,
  onRefetch,
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const [trip, setTrip] = useState(init);
  const [tab, setTab] = useState("overview");
  const [members, setMembers] = useState([]);
  const [mLoad, setMLoad] = useState(true);

  // ── Client Detail Modal State ──
  const [selectedClient, setSelectedClient] = useState(null);
  // Which client-level modal is stacked on top of the client detail modal.
  const [clientModal, setClientModal] = useState(null); // null | "edit" | "task"
  const [savingClient, setSavingClient] = useState(false);

  // ── Client search state ──
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [clientPage, setClientPage] = useState(1);
  const [clientTotalPages, setClientTotalPages] = useState(1);
  const [clientTotal, setClientTotal] = useState(0);
  const [clientLoading, setClientLoading] = useState(false);
  const CLIENT_LIMIT = 10;
  const [adding, setAdding] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const TABS = ["overview", "accommodation", "tours", "members", "notes"];

  const reload = useCallback(async () => {
    try {
      const fresh = await api.get(`/crm/trips/${trip._id}`);
      setTrip(fresh);
      if (onRefetch) onRefetch();
    } catch (e) {
      toast(e.message, "error");
    }
  }, [trip._id, onRefetch]);

  // Extracted so it can be re-run after a client is edited (names/emails change).
  const loadMembers = useCallback(async () => {
    setMLoad(true);
    try {
      const data = await api.get(`/crm/trip-members/by-trip/${trip._id}`);
      setMembers(data || []);
    } catch {
      setMembers([]);
    } finally {
      setMLoad(false);
    }
  }, [trip._id]);

  useEffect(() => {
    if (tab !== "members") return;
    loadMembers();
  }, [tab, loadMembers]);

  // ── Fetch clients with search + pagination ──
  useEffect(() => {
    if (tab !== "members" || !dropdownOpen) {
      setResults([]);
      return;
    }
    setClientLoading(true);
    const t = setTimeout(() => {
      const params = new URLSearchParams({
        page: clientPage,
        limit: CLIENT_LIMIT,
        ...(query.trim() && { search: query.trim() }),
      });
      api
        .get(`/crm/clients?${params}`)
        .then((r) => {
          setResults(r.data || []);
          setClientTotal(r.total ?? r.meta?.total ?? 0);
          setClientTotalPages(r.totalPages ?? r.meta?.totalPages ?? 1);
        })
        .catch(() => {
          setResults([]);
          setClientTotal(0);
          setClientTotalPages(1);
        })
        .finally(() => setClientLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [query, clientPage, tab, dropdownOpen]);

  useEffect(() => {
    setClientPage(1);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── Add a client by linking ONE of their trips to this group trip ──────────
  // linking = the client (with trips) whose trip is being chosen, or null.
  const [linking, setLinking] = useState(null);

  const startLink = async (clientOrId) => {
    const id = clientOrId?._id || clientOrId;
    try {
      const full = await api.get(`/crm/clients/${id}`); // fresh trips
      setLinking(full);
      setDropdownOpen(false);
    } catch (e) {
      toast(e.message, "error");
    }
  };

  // idx = index of the client's trip to link, or "new" to create one from this package.
  const linkTrip = async (c, idx) => {
    setAdding(true);
    try {
      const isMember = members.some((m) => (m.clientId?._id || m.clientId) === c._id);
      if (!isMember) await api.post("/crm/trip-members", { tripId: trip._id, clientId: c._id });
      if (idx === "new") {
        // New trip copied from this package (destinations, type, status, dates + link).
        await api.post(`/crm/clients/${c._id}/trips`, normaliseDestinations(tripTemplateToTripData(trip)));
      } else {
        // Link the chosen trip and align its status with this group trip.
        await api.patch(`/crm/clients/${c._id}/trips/${idx}`, {
          tripPackageId: trip._id,
          progressStatus: TRIP_STATUS_MAP[trip.status] || "planning",
        });
      }
      toast(`${c.fullName?.split(" ")[0] || "Client"} added to ${trip.tripName} ✓`);
      setLinking(null);
      setQuery("");
      setResults([]);
      setClientPage(1);
      const upd = await api.get(`/crm/trip-members/by-trip/${trip._id}`);
      setMembers(upd || []);
      reload();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setAdding(false);
    }
  };


  const removeMember = async (mid) => {
    if (!(await confirm({ title: "Remove member?", message: "Remove this member from the trip?", confirmText: "Remove" }))) return;
    try {
      await api.delete(`/crm/trip-members/${mid}`);
      toast("Removed");
      setMembers((p) => p.filter((m) => m._id !== mid));
      reload();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  const openClientDetail = async (clientId) => {
    if (!clientId) return;
    try {
      const full = await api.get(`/crm/clients/${clientId}`);
      setSelectedClient(full);
    } catch (e) {
      toast(e.message, "error");
    }
  };

  // Refresh the open client after a change (trip added/edited, profile saved).
  const refreshSelectedClient = useCallback(async () => {
    if (!selectedClient?._id) return;
    try {
      const fresh = await api.get(`/crm/clients/${selectedClient._id}`);
      setSelectedClient(fresh);
    } catch {
      /* non-fatal */
    }
  }, [selectedClient?._id]);

  // Personal-fields-only edit — never sends `trips`, so trips are preserved.
  const doEditClient = async (payload) => {
    setSavingClient(true);
    try {
      await api.patch(`/crm/clients/${selectedClient._id}`, payload);
      toast("Client saved ✓");
      setClientModal(null);
      await refreshSelectedClient();
      loadMembers(); // member chips show the client's name/email
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSavingClient(false);
    }
  };

  const quickStatus = async (s) => {
    try {
      const res = await api.patch(`/crm/trips/${trip._id}`, { status: s });
      const p = res?.propagation;
      if (p) {
        toast(`Status → ${s} · ${p.updated} client trip${p.updated !== 1 ? "s" : ""} updated`);
        if (p.unmatched?.length)
          toast(`No linked trip for: ${p.unmatched.join(", ")} — use “Link a trip” under Members`, "warn");
      } else {
        toast(`Status → ${s}`);
      }
      reload();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  const duration =
    trip.startDate && trip.endDate
      ? Math.ceil(
          (new Date(trip.endDate) - new Date(trip.startDate)) / 86400000,
        )
      : null;
  const typeInfo = getTripTypeInfo(trip.tripType);

  return (
    <div className="flex flex-col" style={{ minHeight: "72vh" }}>
      <div
        className={`bg-gradient-to-br ${HERO[trip.status] || "from-slate-500 to-slate-600"} px-6 pt-6 pb-5 rounded-t-2xl flex-shrink-0`}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0 pr-3">
            <div className="flex items-center gap-2 mb-1.5">
              <p className="text-white/60 text-[10px] uppercase tracking-widest font-bold">
                Trip Package
              </p>
              {trip.tripType && (
                <span className="bg-white/20 backdrop-blur text-white text-[10px] px-2 py-0.5 rounded-full font-bold border border-white/20">
                  {typeInfo.emoji} {typeInfo.label}
                </span>
              )}
            </div>
            <h2
              className="text-2xl font-bold text-white leading-tight mb-2.5"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              {trip.tripName}
            </h2>
            <div className="flex flex-wrap gap-1.5">
              {trip.destinations?.map((d) => (
                <span
                  key={d}
                  className="bg-white/20 backdrop-blur text-white text-xs px-2.5 py-0.5 rounded-full font-semibold border border-white/20"
                >
                  {d}
                </span>
              ))}
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={onEdit}
              title="Edit trip"
              className="w-8 h-8 rounded-xl bg-white/20 hover:bg-white/35 flex items-center justify-center text-white transition-colors"
            >
              <Icon d={I.edit} size={14} />
            </button>
            <button
              onClick={onClose}
              title="Close"
              className="w-8 h-8 rounded-xl bg-white/20 hover:bg-white/35 flex items-center justify-center text-white transition-colors"
            >
              <Icon d={I.close} size={14} />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {[
            {
              label: "Departs",
              val: trip.startDate
                ? new Date(trip.startDate).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                : "–",
            },
            {
              label: "Returns",
              val: trip.endDate
                ? new Date(trip.endDate).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                : "–",
            },
            { label: "Duration", val: duration ? `${duration} days` : "–" },
            {
              label: "Pax",
              val: trip.membersCount != null ? `${trip.membersCount} pax` : "–",
            },
          ].map((s) => (
            <div
              key={s.label}
              className="bg-white/15 backdrop-blur rounded-xl px-3 py-2.5 text-center border border-white/10"
            >
              <p className="text-white font-bold text-sm leading-tight">
                {s.val}
              </p>
              <p className="text-white/55 text-[10px] uppercase tracking-wider mt-0.5 font-semibold">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex border-b border-slate-100 bg-white overflow-x-auto flex-shrink-0">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-3 text-xs font-bold uppercase tracking-wider whitespace-nowrap border-b-2 transition-all flex-shrink-0 ${tab === t ? "border-[rgb(var(--brand-rgb))] text-[rgb(var(--brand-rgb))]" : "border-transparent text-slate-400 hover:text-slate-600 hover:bg-slate-50"}`}
          >
            {t}
            {t === "members" && trip.membersCount
              ? ` (${trip.membersCount})`
              : ""}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        {tab === "overview" && (
          <>
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2.5">
                Update Status
              </p>
              <div className="flex gap-2 flex-wrap">
                {[
                  "planning",
                  "confirmed",
                  "ongoing",
                  "completed",
                  "cancelled",
                ].map((s) => (
                  <button
                    key={s}
                    onClick={() => quickStatus(s)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-semibold capitalize transition-all border ${trip.status === s ? `${STATUS_PILL[s] || "bg-slate-100 text-slate-600"} border-transparent ring-2 ring-offset-1 ring-current` : "border-slate-200 text-slate-400 hover:border-slate-300 bg-white hover:text-slate-600"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: "Trip Name", val: trip.tripName },
                {
                  label: "Trip Type",
                  val: trip.tripType
                    ? `${typeInfo.emoji} ${typeInfo.label}`
                    : "–",
                },
                { label: "Destinations", val: trip.destinations?.join(", ") },
                { label: "Start Date", val: fmtDate(trip.startDate) },
                { label: "End Date", val: fmtDate(trip.endDate) },
                { label: "Duration", val: duration ? `${duration} days` : "–" },
                {
                  label: "Total Pax",
                  val:
                    trip.membersCount != null
                      ? `${trip.membersCount} client${trip.membersCount !== 1 ? "s" : ""}`
                      : "–",
                },
              ].map((item) => (
                <div key={item.label} className="p-3.5 bg-slate-50 rounded-xl">
                  <dt className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">
                    {item.label}
                  </dt>
                  <dd className="text-sm font-semibold text-slate-800">
                    {item.val || "–"}
                  </dd>
                </div>
              ))}
            </div>
            <div className="pt-3 border-t border-slate-100">
              <Btn variant="danger" size="sm" onClick={onDeleted}>
                <Icon d={I.trash} size={13} /> Delete Trip
              </Btn>
            </div>
          </>
        )}

        {tab === "accommodation" && (
          <>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              Hotels & Accommodation
            </p>
            {!trip.hotel?.length ? (
              <div className="flex flex-col items-center justify-center py-16 text-center bg-slate-50 rounded-2xl">
                <span className="text-5xl mb-3">🏨</span>
                <p className="font-semibold text-slate-600 mb-1">
                  No hotels added
                </p>
                <p className="text-xs text-slate-400">
                  Edit the trip to add accommodation
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {trip.hotel.map((h, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-4 p-4 bg-white border border-slate-100 rounded-2xl hover:border-amber-200 hover:shadow-sm transition-all"
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-400 flex items-center justify-center text-2xl flex-shrink-0 shadow-sm">
                      🏨
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-800">{h}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {trip.destinations?.join(", ") || "–"} ·{" "}
                        {fmtDate(trip.startDate)} – {fmtDate(trip.endDate)}
                      </p>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 flex-shrink-0">
                      Hotel
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "tours" && (
          <>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              Tours & Activities
            </p>
            {!trip.toursBooked?.length ? (
              <div className="flex flex-col items-center justify-center py-16 text-center bg-slate-50 rounded-2xl">
                <span className="text-5xl mb-3">🗺️</span>
                <p className="font-semibold text-slate-600 mb-1">
                  No tours booked
                </p>
                <p className="text-xs text-slate-400">
                  Edit the trip to add tours or activities
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {trip.toursBooked.map((t, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-4 p-4 bg-white border border-slate-100 rounded-2xl hover:border-sky-200 hover:shadow-sm transition-all"
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center text-2xl flex-shrink-0 shadow-sm">
                      🗺️
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-800">{t}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {trip.destinations?.slice(0, 1).join(" ") || "–"} ·
                        Activity
                      </p>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-sky-100 text-sky-700 flex-shrink-0">
                      Tour
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "members" && (
          <>
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2.5">
                Add Client to Trip
              </p>
              <div className="relative" ref={dropdownRef}>
                <div className="relative">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#94a3b8"
                    strokeWidth="2"
                    className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                  >
                    <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      if (!dropdownOpen) setDropdownOpen(true);
                    }}
                    onFocus={() => setDropdownOpen(true)}
                    placeholder="Type client name to search and add…"
                    className="w-full pl-8 pr-10 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))]"
                  />
                  <button
                    type="button"
                    onClick={() => setDropdownOpen((p) => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    title={dropdownOpen ? "Collapse list" : "Show clients"}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className={`transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                </div>

                {dropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
                    <div className="max-h-52 overflow-y-auto">
                      {clientLoading ? (
                        <div className="flex items-center justify-center py-6 gap-2 text-slate-400">
                          <div className="w-4 h-4 border-2 border-[rgb(var(--brand-rgb))] border-t-transparent rounded-full animate-spin" />
                          <span className="text-xs">Loading clients…</span>
                        </div>
                      ) : results.length === 0 ? (
                        <div className="px-4 py-5 text-sm text-slate-400 text-center">
                          {query
                            ? `No clients match "${query}"`
                            : "No clients found"}
                        </div>
                      ) : (
                        results.map((c) => (
                          <button
                            key={c._id}
                            onClick={() => startLink(c)}
                            disabled={adding}
                            className="w-full text-left px-4 py-3 hover:bg-[rgb(var(--brand-rgb)/0.05)] text-sm flex items-center gap-3 border-b border-slate-50 last:border-0 transition-colors"
                          >
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[rgb(var(--brand-rgb))] to-[rgb(var(--brand-accent-rgb))] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                              {c.fullName?.charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-slate-700 truncate">
                                {c.fullName}
                              </p>
                              <p className="text-xs text-slate-400">
                                {c.email}
                              </p>
                            </div>
                            <span className="text-xs text-[rgb(var(--brand-rgb))] font-bold flex-shrink-0">
                              + Add
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                    {!clientLoading && clientTotalPages > 1 && (
                      <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100 bg-slate-50">
                        <button
                          disabled={clientPage <= 1}
                          onClick={() => setClientPage((p) => p - 1)}
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-500 hover:bg-white hover:text-[rgb(var(--brand-rgb))] disabled:opacity-30 disabled:cursor-not-allowed transition-colors border border-slate-200 disabled:border-slate-100"
                        >
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                          >
                            <path d="M15 18l-6-6 6-6" />
                          </svg>
                        </button>
                        <span className="text-[11px] text-slate-400 font-semibold">
                          Page {clientPage} of {clientTotalPages}
                          {clientTotal > 0 && (
                            <span className="ml-1 text-slate-300">
                              · {clientTotal} total
                            </span>
                          )}
                        </span>
                        <button
                          disabled={clientPage >= clientTotalPages}
                          onClick={() => setClientPage((p) => p + 1)}
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-500 hover:bg-white hover:text-[rgb(var(--brand-rgb))] disabled:opacity-30 disabled:cursor-not-allowed transition-colors border border-slate-200 disabled:border-slate-100"
                        >
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                          >
                            <path d="M9 18l6-6-6-6" />
                          </svg>
                        </button>
                      </div>
                    )}
                    {!clientLoading &&
                      results.length > 0 &&
                      clientTotalPages <= 1 &&
                      clientTotal > 0 && (
                        <div className="px-3 py-1.5 border-t border-slate-100 bg-slate-50">
                          <span className="text-[11px] text-slate-400">
                            Showing {results.length} of {clientTotal} client
                            {clientTotal !== 1 ? "s" : ""}
                          </span>
                        </div>
                      )}
                  </div>
                )}
              </div>

              {/* Added members chips - CLICKABLE */}
              {members.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {members.map((m) => (
                    <span
                      key={m._id}
                      onClick={() => openClientDetail(m.clientId?._id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[rgb(var(--brand-rgb)/0.1)] text-[rgb(var(--brand-rgb))] text-xs font-semibold rounded-full border border-[rgb(var(--brand-rgb)/0.2)] cursor-pointer hover:bg-[rgb(var(--brand-rgb)/0.2)] transition-colors"
                    >
                      {m.clientId?.fullName?.split(" ")[0]}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeMember(m._id);
                        }}
                        className="ml-1 text-[rgb(var(--brand-rgb)/0.6)] hover:text-rose-500 transition-colors"
                        title="Remove"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* ── Choose which of the client's trips belongs to this group trip ── */}
            {linking && (() => {
              const first = linking.fullName?.split(" ")[0] || "this client";
              const list = (linking.trips || []).map((t, i) => [t, i]).reverse(); // newest first
              const suggested = findTripIndexForPackage(linking.trips || [], trip);
              return (
                <div className="rounded-xl border border-[rgb(var(--brand-rgb)/0.3)] bg-[rgb(var(--brand-rgb)/0.04)] p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-bold text-slate-800">Which of {first}'s trips is {trip.tripName}?</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        The chosen trip is linked to this group trip and follows its status.
                      </p>
                    </div>
                    <button onClick={() => setLinking(null)} className="text-slate-400 hover:text-slate-600 text-sm font-bold" title="Cancel">✕</button>
                  </div>
                  {list.length > 0 && (
                    <div className="space-y-1.5">
                      {list.map(([t, i]) => {
                        const here = t.tripPackageId === trip._id;
                        const elsewhere = t.tripPackageId && !here;
                        return (
                          <button
                            key={i}
                            onClick={() => linkTrip(linking, i)}
                            disabled={adding}
                            className={`w-full text-left px-3 py-2.5 rounded-lg border bg-white text-xs flex items-center gap-2 transition-colors disabled:opacity-60 ${
                              i === suggested ? "border-[rgb(var(--brand-rgb))]" : "border-slate-200 hover:border-slate-300"}`}
                          >
                            <span className="flex-1 min-w-0 truncate font-semibold text-slate-700">{tripLabelLong(t, i)}</span>
                            {here && <span className="text-[10px] font-bold text-emerald-600 flex-shrink-0">already linked</span>}
                            {elsewhere && <span className="text-[10px] font-bold text-amber-600 flex-shrink-0">linked to another group trip</span>}
                            {!here && i === suggested && <span className="text-[10px] font-bold text-[rgb(var(--brand-rgb))] flex-shrink-0">best match</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <button
                    onClick={() => linkTrip(linking, "new")}
                    disabled={adding}
                    className="w-full text-left px-3 py-2.5 rounded-lg border border-dashed border-slate-300 hover:border-[rgb(var(--brand-rgb))] text-xs font-bold text-[rgb(var(--brand-rgb))] bg-white disabled:opacity-60"
                  >
                    ＋ {list.length ? "None of these — create" : "Create"} a new trip for {first} from {trip.tripName}
                  </button>
                </div>
              );
            })()}

            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">
                Passengers ({members.length})
              </p>
              {mLoad ? (
                <div className="flex justify-center py-8">
                  <div className="w-6 h-6 border-2 border-[rgb(var(--brand-rgb))] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : !members.length ? (
                <div className="flex flex-col items-center justify-center py-14 text-center bg-slate-50 rounded-2xl">
                  <span className="text-4xl mb-3">👥</span>
                  <p className="font-semibold text-slate-600 mb-1">
                    No passengers yet
                  </p>
                  <p className="text-xs text-slate-400">
                    Search above to add clients to this trip
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {members.map((m, idx) => (
                    <div
                      key={m._id}
                      onClick={() => openClientDetail(m.clientId?._id)}
                      className="flex items-center gap-3 p-3.5 rounded-xl border border-slate-100 hover:border-slate-200 bg-white transition-colors cursor-pointer hover:shadow-sm"
                    >
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[rgb(var(--brand-rgb))] to-[rgb(var(--brand-accent-rgb))] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        {m.clientId?.fullName?.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-slate-800">
                          {m.clientId?.fullName}
                        </p>
                        <p className="text-xs text-slate-400">
                          {m.clientId?.email}
                          {m.clientId?.phone ? ` · ${m.clientId.phone}` : ""}
                        </p>
                        {(() => {
                          const ts = m.clientId?.trips || [];
                          const i = findTripIndexForPackage(ts, trip);
                          const t = ts[i];
                          if (t && t.tripPackageId === trip._id)
                            return <p className="text-[10px] font-semibold text-emerald-600 mt-0.5 truncate">✓ {tripLabelLong(t, i)}</p>;
                          return (
                            <button
                              onClick={(e) => { e.stopPropagation(); startLink(m.clientId?._id); }}
                              className="text-[10px] font-bold text-amber-600 hover:underline mt-0.5"
                            >
                              {t ? `Likely: ${tripLabelLong(t, i)} · Confirm link` : "No trip linked · Link a trip"}
                            </button>
                          );
                        })()}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-[10px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full font-semibold">
                          Pax {idx + 1}
                        </span>
                        <Btn
                          variant="danger"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeMember(m._id);
                          }}
                        >
                          <Icon d={I.trash} size={12} />
                        </Btn>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {tab === "notes" && (
          <>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              Trip Notes
            </p>
            {trip.notes ? (
              <div className="p-5 bg-amber-50 border border-amber-100 rounded-2xl">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">📝</span>
                  <p className="text-xs font-bold text-amber-600 uppercase tracking-wider">
                    Internal Notes
                  </p>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                  {trip.notes}
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center bg-slate-50 rounded-2xl">
                <span className="text-5xl mb-3">📝</span>
                <p className="font-semibold text-slate-600 mb-1">
                  No notes yet
                </p>
                <p className="text-xs text-slate-400">
                  Edit the trip to add internal notes
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Client Detail Modal ── */}
      {selectedClient && (
        <ClientDetailModal
          client={selectedClient}
          onClose={() => {
            setSelectedClient(null);
            setClientModal(null);
          }}
          onEdit={() => setClientModal("edit")}
          onAddTask={() => setClientModal("task")}
          onClientChanged={refreshSelectedClient}
          focusTrip={trip}
        />
      )}

      {/* ── Edit the client's personal details (stacks above the detail modal) ── */}
      {clientModal === "edit" && selectedClient && (
        <ClientFormModal
          initial={selectedClient}
          onSubmit={doEditClient}
          onClose={() => setClientModal(null)}
          saving={savingClient}
        />
      )}

      {/* ── Add a task for the client ── */}
      {clientModal === "task" && selectedClient && (
        <QuickTaskModal
          clientId={selectedClient._id}
          clientName={selectedClient.fullName}
          onDone={() => {
            setClientModal(null);
            refreshSelectedClient();
          }}
          onClose={() => setClientModal(null)}
        />
      )}
    </div>
  );
}

// ─── TRIPS TAB ────────────────────────────────────────────────────────────────
export function TripsTab() {
  const toast = useToast();
  const confirm = useConfirm();
  const [statusFilter, setStatusFilter] = useState("all");
  const [tripTypeFilter, setTripTypeFilter] = useState("all");
  const [modal, setModal] = useState(null);
  const [sel, setSel] = useState(null);
  const [saving, setSaving] = useState(false);

  const filters = useMemo(
    () => ({
      ...(statusFilter !== "all" && { status: statusFilter }),
      ...(tripTypeFilter !== "all" && { tripType: tripTypeFilter }),
    }),
    [statusFilter, tripTypeFilter],
  );

  // ✅ FIXED: Keep `data:` alias to match your hook's return shape
  const {
    data: trips,
    total,
    page,
    totalPages,
    loading,
    error,
    setPage,
    refetch,
  } = usePaginatedtrips("/crm/trips", filters);

  const doCreate = async (f) => {
    setSaving(true);
    try {
      await api.post("/crm/trips", f);
      toast("Trip created");
      setModal(null);
      refetch();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const doEdit = async (f) => {
    setSaving(true);
    try {
      await api.patch(`/crm/trips/${sel._id}`, f);
      toast("Trip updated");
      setModal(null);
      setSel((p) => ({ ...p, ...f }));
      refetch();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (id) => {
    if (!(await confirm({ title: "Delete trip?", message: "This trip package will be permanently removed.", confirmText: "Delete trip" }))) return;
    try {
      await api.delete(`/crm/trips/${id}`);
      toast("Trip deleted");
      setModal(null);
      refetch();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  const openDetail = async (t) => {
    try {
      const full = await api.get(`/crm/trips/${t._id}`);
      setSel(full);
      setModal("detail");
    } catch (e) {
      toast(e.message, "error");
    }
  };

  const handleStatusFilter = (s) => {
    setStatusFilter(s);
    setPage(1);
  };
  const handleTypeFilter = (s) => {
    setTripTypeFilter(s);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
          Trips
        </h1>
        <Btn variant="primary" onClick={() => setModal("create")}>
          <Icon d={I.plus} size={16} /> New Trip
        </Btn>
      </div>

      <div className="space-y-3 p-4 bg-slate-50 rounded-2xl border border-slate-100">
        <div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
            Status
          </p>
          <div className="flex gap-2 flex-wrap">
            {[
              "all",
              "planning",
              "confirmed",
              "ongoing",
              "completed",
              "cancelled",
            ].map((s) => (
              <button
                key={s}
                onClick={() => handleStatusFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all capitalize ${statusFilter === s ? "bg-[rgb(var(--brand-rgb))] text-white shadow-sm" : "bg-white border border-slate-200 text-slate-500 hover:bg-slate-50 hover:border-slate-300"}`}
              >
                {s === "all" ? "All Status" : s}
              </button>
            ))}
          </div>
        </div>
        <div className="border-t border-slate-200" />
        <div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
            Trip Type
          </p>
          <div className="flex gap-2 flex-wrap">
            {TRIP_TYPES.map(({ value, label, emoji }) => (
              <button
                key={value}
                onClick={() => handleTypeFilter(value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${tripTypeFilter === value ? "bg-[rgb(var(--brand-rgb))] text-white shadow-sm" : "bg-white border border-slate-200 text-slate-500 hover:bg-slate-50 hover:border-slate-300"}`}
              >
                {value === "all" ? "All Types" : `${emoji} ${label}`}
              </button>
            ))}
          </div>
        </div>
        {(statusFilter !== "all" || tripTypeFilter !== "all") && (
          <div className="flex items-center gap-2 pt-1">
            <span className="text-[11px] text-slate-400">Filtering by:</span>
            {statusFilter !== "all" && (
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[rgb(var(--brand-rgb)/0.1)] text-[rgb(var(--brand-rgb))] capitalize">
                {statusFilter}
              </span>
            )}
            {tripTypeFilter !== "all" && (
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[rgb(var(--brand-rgb)/0.1)] text-[rgb(var(--brand-rgb))]">
                {getTripTypeInfo(tripTypeFilter).emoji}{" "}
                {getTripTypeInfo(tripTypeFilter).label}
              </span>
            )}
            <button
              onClick={() => {
                setStatusFilter("all");
                setTripTypeFilter("all");
                setPage(1);
              }}
              className="text-[11px] text-slate-400 hover:text-rose-500 font-bold transition-colors ml-1"
            >
              ✕ Clear
            </button>
          </div>
        )}
      </div>

      {error && <ApiError msg={error} onRetry={refetch} />}
      {loading && <Spinner full />}

      {!loading && !error && (
        <>
          {!trips || trips.length === 0 ? (
            <Empty
              icon="trips"
              title="No trips found"
              sub={
                statusFilter !== "all" || tripTypeFilter !== "all"
                  ? "Try adjusting your filters"
                  : "Create your first trip package"
              }
              action={
                <Btn variant="primary" onClick={() => setModal("create")}>
                  <Icon d={I.plus} size={16} /> New Trip
                </Btn>
              }
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {trips.map((t) => {
                const dur =
                  t.startDate && t.endDate
                    ? Math.ceil(
                        (new Date(t.endDate) - new Date(t.startDate)) /
                          86400000,
                      )
                    : null;
                const tInfo = getTripTypeInfo(t.tripType);
                return (
                  <Card
                    key={t._id}
                    className="overflow-hidden hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 cursor-pointer group"
                    onClick={() => openDetail(t)}
                  >
                    <div
                      className={`h-2 bg-gradient-to-r ${SBGR[t.status] || "from-slate-300 to-slate-400"}`}
                    />
                    <div className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0 mr-2">
                          <h3 className="font-bold text-slate-800 text-base leading-tight mb-1.5 group-hover:text-[rgb(var(--brand-rgb))] transition-colors">
                            {t.tripName}
                          </h3>
                          <div className="flex flex-wrap gap-1">
                            {t.destinations?.map((d) => (
                              <span
                                key={d}
                                className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-md"
                              >
                                {d}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                          <Badge
                            label={t.status}
                            colorClass={
                              SC[t.status] || "bg-slate-100 text-slate-500"
                            }
                          />
                          {t.tripType && (
                            <span
                              className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${TRIP_TYPE_PILL[t.tripType] || "bg-slate-100 text-slate-500"}`}
                            >
                              {tInfo.emoji} {tInfo.label}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        <div className="bg-slate-50 rounded-lg p-2 text-center">
                          <p className="text-xs font-bold text-slate-700">
                            {t.startDate
                              ? new Date(t.startDate).toLocaleDateString(
                                  "en-GB",
                                  { day: "2-digit", month: "short" },
                                )
                              : "–"}
                          </p>
                          <p className="text-[10px] text-slate-400">Depart</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-2 text-center">
                          <p className="text-xs font-bold text-slate-700">
                            {dur ? `${dur}d` : "–"}
                          </p>
                          <p className="text-[10px] text-slate-400">Days</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-2 text-center">
                          <p className="text-xs font-bold text-slate-700">
                            {t.membersCount ?? 0}
                          </p>
                          <p className="text-[10px] text-slate-400">Pax</p>
                        </div>
                      </div>
                      {t.hotel?.length > 0 && (
                        <div className="mb-2 flex flex-wrap gap-1">
                          {t.hotel.slice(0, 2).map((h) => (
                            <span
                              key={h}
                              className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-md"
                            >
                              🏨 {h}
                            </span>
                          ))}
                          {t.hotel.length > 2 && (
                            <span className="text-xs text-slate-400 self-center">
                              +{t.hotel.length - 2} more
                            </span>
                          )}
                        </div>
                      )}
                      {t.toursBooked?.length > 0 && (
                        <div className="mb-3 flex flex-wrap gap-1">
                          {t.toursBooked.slice(0, 2).map((h) => (
                            <span
                              key={h}
                              className="text-xs bg-sky-50 text-sky-700 px-2 py-0.5 rounded-md"
                            >
                              🗺️ {h}
                            </span>
                          ))}
                          {t.toursBooked.length > 2 && (
                            <span className="text-xs text-slate-400 self-center">
                              +{t.toursBooked.length - 2} more
                            </span>
                          )}
                        </div>
                      )}
                      <div
                        className="flex gap-2 pt-3 border-t border-slate-100"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Btn
                          variant="secondary"
                          size="sm"
                          className="flex-1"
                          onClick={() => openDetail(t)}
                        >
                          <Icon d={I.eye} size={13} /> View Details
                        </Btn>
                        <Btn
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSel(t);
                            setModal("edit");
                          }}
                        >
                          <Icon d={I.edit} size={13} />
                        </Btn>
                        <Btn
                          variant="danger"
                          size="sm"
                          onClick={() => doDelete(t._id)}
                        >
                          <Icon d={I.trash} size={13} />
                        </Btn>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
          <Pages
            page={page}
            totalPages={totalPages}
            total={total}
            onPage={setPage}
            loading={loading}
          />
        </>
      )}

      {modal === "create" && (
        <Modal title="Create New Trip" onClose={() => setModal(null)} wide>
          <TripForm
            onSubmit={doCreate}
            onClose={() => setModal(null)}
            saving={saving}
          />
        </Modal>
      )}
      {modal === "edit" && sel && (
        <Modal title="Edit Trip" onClose={() => setModal(null)} wide>
          <TripForm
            initial={sel}
            onSubmit={doEdit}
            onClose={() => setModal(null)}
            saving={saving}
          />
        </Modal>
      )}

      {modal === "detail" && sel && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center p-4"
          style={{
            background: "rgba(15,23,42,.6)",
            backdropFilter: "blur(6px)",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setModal(null);
          }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
            style={{ animation: "modalIn .2s ease" }}
          >
            <TripDetailModal
              trip={sel}
              onClose={() => setModal(null)}
              onEdit={() => setModal("edit")}
              onDeleted={() => doDelete(sel._id)}
              onRefetch={refetch}
            />
          </div>
        </div>
      )}
    </div>
  );
}